import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Cpu,
  Timer,
  Database,
  AlertTriangle,
  AlertCircle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import {
  type Site,
  type DiagnosticsHealth,
  type CronJob,
  type DatabaseInfo,
  type ErrorEntry,
} from './shared'

interface DiagnosticsTabProps {
  site: Site
}

export function DiagnosticsTab({ site }: DiagnosticsTabProps) {
  const [health, setHealth] = useState<DiagnosticsHealth | null>(null)
  const [cron, setCron] = useState<CronJob[]>([])
  const [database, setDatabase] = useState<DatabaseInfo | null>(null)
  const [errors, setErrors] = useState<ErrorEntry[]>([])
  const [showErrors, setShowErrors] = useState(false)
  const [loadingStates, setLoadingStates] = useState({
    health: true,
    cron: true,
    database: true,
    errors: true,
  })

  // Fetch all diagnostics data in parallel
  useEffect(() => {
    // Fetch health
    fetch(`/api/auth-server/sites/${site.domain}/remote/diagnostics/health`)
      .then(r => r.ok ? r.json() as Promise<DiagnosticsHealth> : null)
      .then(data => {
        if (data) setHealth(data)
        setLoadingStates(prev => ({ ...prev, health: false }))
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, health: false })))

    // Fetch cron
    fetch(`/api/auth-server/sites/${site.domain}/remote/diagnostics/cron`)
      .then(r => r.ok ? r.json() as Promise<{ tasks?: CronJob[] }> : null)
      .then(data => {
        if (data?.tasks) setCron(data.tasks)
        setLoadingStates(prev => ({ ...prev, cron: false }))
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, cron: false })))

    // Fetch database
    fetch(`/api/auth-server/sites/${site.domain}/remote/diagnostics/database`)
      .then(r => r.ok ? r.json() as Promise<DatabaseInfo> : null)
      .then(data => {
        if (data) setDatabase(data)
        setLoadingStates(prev => ({ ...prev, database: false }))
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, database: false })))

    // Fetch errors
    fetch(`/api/auth-server/sites/${site.domain}/remote/diagnostics/errors`)
      .then(r => r.ok ? r.json() as Promise<{ errors?: ErrorEntry[] }> : null)
      .then(data => {
        if (data?.errors) setErrors(data.errors)
        setLoadingStates(prev => ({ ...prev, errors: false }))
      })
      .catch(() => setLoadingStates(prev => ({ ...prev, errors: false })))
  }, [site.domain])

  // Get overdue cron jobs
  const overdueCronJobs = cron.filter(job => job.overdue)

  // Count Site Health test results by status
  const healthTests = health?.tests ? Object.values(health.tests) : []
  const criticalCount = healthTests.filter(t => t.status === 'critical').length
  const recommendedCount = healthTests.filter(t => t.status === 'recommended').length

  return (
    <div className="space-y-6">
      {/* Health Status Card */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <Cpu className="h-4 w-4 text-muted-foreground" />
          Health Status
        </div>
        {loadingStates.health ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : health ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">PHP</span>
                <span className="font-mono">{health.server?.php_version || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Memory</span>
                <span className="font-mono">{health.server?.php_memory_limit || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Exec</span>
                <span className="font-mono">{health.server?.max_execution ? `${health.server.max_execution}s` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Upload</span>
                <span className="font-mono">{health.server?.upload_max || '-'}</span>
              </div>
            </div>

            {/* Disk Space */}
            {health.disk?.free_gb != null && health.disk?.total_gb != null && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Disk Space</span>
                  <span className="font-mono">
                    {health.disk.free_gb.toFixed(1)} GB free of {health.disk.total_gb.toFixed(1)} GB
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${health.disk.used_pct || 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Site Health */}
            {healthTests.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Site Health:</span>
                {criticalCount > 0 && (
                  <Badge variant="destructive">
                    {criticalCount} critical
                  </Badge>
                )}
                {recommendedCount > 0 && (
                  <Badge variant="outline">
                    {recommendedCount} recommended
                  </Badge>
                )}
                {criticalCount === 0 && recommendedCount === 0 && (
                  <Badge variant="outline" className="border-green-500 text-green-600">
                    All good
                  </Badge>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load health data</p>
        )}
      </div>

      {/* Cron Jobs Card */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium">
            <Timer className="h-4 w-4 text-muted-foreground" />
            Cron Jobs
          </div>
          {overdueCronJobs.length > 0 && (
            <Badge variant="destructive">
              {overdueCronJobs.length} overdue
            </Badge>
          )}
        </div>
        {loadingStates.cron ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : cron.length > 0 ? (
          <div className="space-y-2">
            {cron.slice(0, 5).map((job, i) => (
              <div
                key={`${job.hook}-${i}`}
                className={`flex items-center justify-between p-2 rounded text-sm ${
                  job.overdue ? 'bg-destructive/10' : 'bg-muted/50'
                }`}
              >
                <div className="truncate flex-1 min-w-0">
                  <span className="font-mono text-xs truncate block">{job.hook}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <Badge variant={job.overdue ? 'destructive' : 'outline'} className="text-xs">
                    {job.overdue ? 'Overdue' : job.next_run_diff || job.schedule}
                  </Badge>
                </div>
              </div>
            ))}
            {cron.length > 5 && (
              <p className="text-xs text-muted-foreground text-center">
                +{cron.length - 5} more scheduled tasks
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No cron jobs found</p>
        )}
      </div>

      {/* Database Card */}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <Database className="h-4 w-4 text-muted-foreground" />
          Database
        </div>
        {loadingStates.database ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : database ? (
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Size</span>
              <span className="font-mono">
                {database.total_size_mb ? `${database.total_size_mb.toFixed(1)} MB` : '-'}
              </span>
            </div>

            {database.issues && database.issues.length > 0 && database.issues.map((issue, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 p-2 rounded text-sm ${
                  issue.type === 'warning'
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{issue.message}</span>
              </div>
            ))}

            {database.wp_tables && database.wp_tables.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Largest tables:</div>
                {database.wp_tables
                  .sort((a, b) => b.size_mb - a.size_mb)
                  .slice(0, 3)
                  .map(table => (
                    <div key={table.name} className="flex justify-between text-xs">
                      <span className="font-mono truncate flex-1">{table.name}</span>
                      <span className="text-muted-foreground ml-2">
                        {table.size_mb.toFixed(1)} MB ({table.rows.toLocaleString()} rows)
                      </span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load database info</p>
        )}
      </div>

      {/* PHP Errors Card (Collapsible) */}
      <div className="rounded-lg border overflow-hidden">
        <button
          onClick={() => setShowErrors(!showErrors)}
          className="flex items-center justify-between w-full p-4 hover:bg-muted/50"
        >
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            PHP Error Log
            {errors.length > 0 && (
              <Badge variant="outline" className="ml-1">
                {errors.length}
              </Badge>
            )}
          </div>
          {showErrors ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {showErrors && (
          <div className="border-t p-4">
            {loadingStates.errors ? (
              <Skeleton className="h-20 w-full" />
            ) : errors.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-auto">
                {errors.slice(0, 10).map((error, i) => (
                  <div
                    key={i}
                    className={`text-xs p-2 rounded whitespace-pre-wrap break-all ${
                      error.level === 'fatal error' || error.level === 'error'
                        ? 'bg-destructive/10 text-destructive'
                        : error.level === 'warning'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {error.date && <span className="text-muted-foreground">{error.date}</span>}
                      {error.level && (
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {error.level}
                        </Badge>
                      )}
                    </div>
                    <div className="font-mono">{error.message}</div>
                    {error.file && (
                      <div className="text-muted-foreground mt-1 text-[10px]">
                        {error.file}{error.line ? `:${error.line}` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No PHP errors in log</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
